import {useCommentEditsContext} from '@github-ui/commenting/CommentEditsContext'
import {IssueCommentComposer} from '@github-ui/commenting/IssueCommentComposer'
import type {MarkdownComposerRef} from '@github-ui/commenting/useMarkdownBody'
import {VALUES as COMMENTING_VALUES} from '@github-ui/commenting/Values'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {IssueBody} from '@github-ui/issue-body/IssueBody'
import {useItemPickersContext} from '@github-ui/item-picker-base/ItemPickersContext'
import {UserNameDisplayContextProvider} from '@github-ui/profile-reference/UserNameDisplayContext'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useBeforeUnload} from '@github-ui/react-router'
import PreloadedQueryBoundary from '@github-ui/relay-preloaded-query-boundary'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {SubIssuesCreateDialog} from '@github-ui/sub-issues/SubIssuesCreateDialog'
import {SubIssuesList} from '@github-ui/sub-issues/SubIssuesList'
import {useSubIssueState} from '@github-ui/sub-issues/SubIssueStateContext'
import {useCanEditSubIssues} from '@github-ui/sub-issues/useCanEditSubIssues'
import {useHasSubIssues} from '@github-ui/sub-issues/useHasSubIssues'
import {ISSUE_EVENTS} from '@github-ui/timeline-items/Events'
import {getHighlightedEventText} from '@github-ui/timeline-items/HighlightedEvent'
import {LABELS} from '@github-ui/timeline-items/Labels'
import {useContainerBreakpoint} from '@github-ui/use-container-breakpoint'
import {Heading} from '@primer/react'
import {Box} from '@primer/styled-react'
import {clsx} from 'clsx'
import {Suspense, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {graphql, type PreloadedQuery, usePreloadedQuery, useQueryLoader} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {CLASS_NAMES} from '../constants/class-names'
import {IDS} from '../constants/ids'
import {TEST_IDS} from '../constants/test-ids'
import {VALUES} from '../constants/values'
import {useInputElementActiveContext} from '../contexts/InputElementActiveContext'
import {useHash} from '../hooks/use-hash'
import type {ItemIdentifier} from '../types/issue'
import type {IssueViewerIssue$key} from './__generated__/IssueViewerIssue.graphql'
import type {IssueViewerSecondaryIssueData$data} from './__generated__/IssueViewerSecondaryIssueData.graphql'
import type {IssueViewerViewer$key} from './__generated__/IssueViewerViewer.graphql'
import type {IssueViewerViewQuery} from './__generated__/IssueViewerViewQuery.graphql'
import {ContentWrapper} from './ContentWrapper'
import {ConvertedToDiscussionBanner} from './ConvertedToDiscussionBanner'
import {CopilotCodingAgentRequestAccessSuccessBanner} from './CopilotAccessRequestSuccessBanner'
import {EmuContributionBlockedBanner} from './EmuContributionBlockedBanner'
import {Header} from './header/Header'
import {IssueSidebar} from './IssueSidebar'
import {IssueTimelineErrorFallback} from './IssueTimelineErrorFallback'
import {IssueTimelineLoading} from './IssueTimelineLoading'
import styles from './IssueViewer.module.css'
import {IssueViewerLoading} from './IssueViewerLoading'
import {useSecondaryQuery} from './IssueViewerSecondaryView'
import {ISSUE_VIEWER_DEFAULT_CONFIG, type OptionConfig} from './OptionConfig'
import {renderIssueViewerErrors} from './shared/IssueViewerError'
import {SignedOutBanner} from './SignedOutBanner'
import {IssueTimeline} from './timeline/IssueTimeline'

export type IssueViewerQueries = {
  issueViewerViewQuery: IssueViewerViewQuery
}

type IssueViewerProps = {
  itemIdentifier: ItemIdentifier
  optionConfig?: OptionConfig
}

type IssueViewerSecondaryDataProps = {
  secondaryIssueData?: IssueViewerSecondaryIssueData$data
  timelinePage?: number
}

type IssueViewerInternalProps = {
  optionConfig: OptionConfig
  issueViewerViewRef: PreloadedQuery<IssueViewerViewQuery>
  containerRef: React.RefObject<HTMLDivElement | null>
} & IssueViewerSecondaryDataProps

type IssueViewerInternalFragmentProps = {
  optionConfig: OptionConfig
  viewerFragment: IssueViewerViewer$key | null
  issueFragment: IssueViewerIssue$key
  containerRef: React.RefObject<HTMLDivElement | null>
  isRepoOwnerEnterpriseManaged?: boolean | null
} & IssueViewerSecondaryDataProps

type IssueViewerWithSecondaryProps = IssueViewerInternalProps & {
  owner: string
  repo: string
  number: number
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const IssueViewerSecondaryIssueDataFragment = graphql`
  fragment IssueViewerSecondaryIssueData on Issue {
    id
    ...HeaderSecondary
    ...HeaderParentTitle
    ...IssueCommentComposerSecondary
    ...IssueTimelineSecondary
    ...IssueSidebarLazySections
    ...IssueSidebarSecondary
    # eslint-disable-next-line relay/must-colocate-fragment-spreads
    ...TaskListStatusFragment
    # eslint-disable-next-line relay/must-colocate-fragment-spreads
    ...TrackedByFragment
    ...IssueBodyHeaderSecondaryFragment
    ...IssueBodyHeaderSecondaryFragment_issue
    ...IssueBodySecondaryFragment
    # sub-issues
    ...SubIssuesList
    ...SubIssuesCreateDialog
    ...HeaderSubIssueSummary @arguments(fetchSubIssues: true)
    ...HeaderBlockedBySummary
    discussion {
      url
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const IssueViewerViewGraphqlQuery = graphql`
  query IssueViewerViewQuery(
    $repo: String!
    $owner: String!
    $number: Int!
    $allowedOwner: String
    $hideTimeline: Boolean = false
    $skip: Int
    $count: Int
  ) {
    repository(name: $repo, owner: $owner) {
      isOwnerEnterpriseManaged
      owner {
        userNameDisplayConfiguration
      }
      issue(number: $number) @required(action: THROW) {
        ...IssueViewerIssue
          @arguments(allowedOwner: $allowedOwner, hideTimeline: $hideTimeline, skip: $skip, count: $count)
      }
    }
    safeViewer {
      ...IssueViewerViewer
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const issueViewerViewFragment = graphql`
  fragment IssueViewerIssue on Issue
  @argumentDefinitions(
    allowedOwner: {type: "String", defaultValue: null}
    hideTimeline: {type: "Boolean", defaultValue: false}
    skip: {type: "Int"}
    count: {type: "Int", defaultValue: 15}
  ) {
    id
    # eslint-disable-next-line relay/unused-fields the updatedAt field is used for an experiment to count the different version of an issue
    updatedAt
    ...Header
    ...IssueBody
    ...IssueCommentComposer
    ...IssueSidebarPrimaryQuery @arguments(allowedOwner: $allowedOwner)
    ...IssueTimelineIssueFragment @skip(if: $hideTimeline) @arguments(skip: $skip, count: $count)
    # sub-issues
    ...HeaderSubIssueSummary @arguments(fetchSubIssues: false)
    ...useHasSubIssues
    ...useCanEditSubIssues
  }
`
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const issueViewerViewerFragment = graphql`
  fragment IssueViewerViewer on User {
    # Is needed to be passed to the IssueSidebar and the SubscriptionSection component in order not to show the notification customization option for EMU
    # eslint-disable-next-line relay/unused-fields
    isEnterpriseManagedUser
    enterpriseManagedEnterpriseId
    login
    ...IssueCommentComposerViewer
    # this is intentional to have the current viewer preloaded for the item pickers
    # eslint-disable-next-line relay/must-colocate-fragment-spreads this is required by the item pickers
    ...AssigneesSectionViewer
  }
`

export function IssueViewer({itemIdentifier, optionConfig = ISSUE_VIEWER_DEFAULT_CONFIG}: IssueViewerProps) {
  const {repo, owner, number} = itemIdentifier
  const urlQuery = ssrSafeLocation.search
  const searchParams = new URLSearchParams(urlQuery)
  const timelinePage = searchParams.get('timeline_page')

  const [issueViewerViewRef, loadIssueViewerView] = useQueryLoader<IssueViewerViewQuery>(
    IssueViewerViewGraphqlQuery,
    optionConfig.preloadedQueries?.issueViewerViewQuery,
  )
  const issueViewerContainerRef = useRef<HTMLDivElement>(null)

  const fetchPolicy = optionConfig.issueQueriesFetchingPolicy?.fetchPolicy

  useEffect(() => {
    if (!optionConfig.preloadedQueries?.issueViewerViewQuery) {
      loadIssueViewerView(
        {
          owner,
          repo,
          number,
          allowedOwner: optionConfig.allowedProjectOwner ?? null,
          hideTimeline: optionConfig.hideTimeline,
          count: VALUES.timelineBackPageSize,
          skip: null,
        },
        {fetchPolicy},
      )
    }
  }, [
    loadIssueViewerView,
    owner,
    repo,
    optionConfig.preloadedQueries?.issueViewerViewQuery,
    optionConfig.allowedProjectOwner,
    fetchPolicy,
    number,
    optionConfig.hideTimeline,
  ])

  if (!issueViewerViewRef) return <IssueViewerLoading optionConfig={optionConfig} />

  return (
    <PreloadedQueryBoundary
      key={`${owner}-${repo}-${number}`}
      onRetry={() =>
        loadIssueViewerView(
          {
            owner,
            repo,
            number,
            allowedOwner: optionConfig.allowedProjectOwner ?? null,
            hideTimeline: optionConfig.hideTimeline,
            count: VALUES.timelineBackPageSize,
            skip: null,
          },
          {fetchPolicy: 'network-only'},
        )
      }
      fallback={renderIssueViewerErrors}
      critical
    >
      <div
        className={clsx(
          styles.issueViewerContainer,
          isFeatureEnabled('issues_title_flex_header_fix') ? styles.mainContainerWithoutFlex : styles.mainContainer,
        )}
        ref={issueViewerContainerRef}
      >
        <Suspense fallback={<IssueViewerLoading optionConfig={optionConfig} />}>
          <IsssueViewerWithSecondary
            owner={owner}
            repo={repo}
            number={number}
            issueViewerViewRef={issueViewerViewRef}
            containerRef={issueViewerContainerRef}
            optionConfig={optionConfig}
            timelinePage={(timelinePage && parseInt(timelinePage, 10)) || undefined}
          />
        </Suspense>
      </div>
    </PreloadedQueryBoundary>
  )
}

function IssueViewerInternal({
  issueViewerViewRef,
  containerRef,
  optionConfig,
  secondaryIssueData,
  timelinePage,
}: IssueViewerInternalProps) {
  const {repository, safeViewer} = usePreloadedQuery<IssueViewerViewQuery>(
    IssueViewerViewGraphqlQuery,
    issueViewerViewRef,
  )

  return repository?.issue ? (
    <UserNameDisplayContextProvider value={repository.owner.userNameDisplayConfiguration}>
      <IssueViewerInternalFragment
        viewerFragment={safeViewer || null}
        issueFragment={repository.issue}
        containerRef={containerRef}
        optionConfig={optionConfig}
        isRepoOwnerEnterpriseManaged={repository.isOwnerEnterpriseManaged}
        secondaryIssueData={secondaryIssueData}
        timelinePage={timelinePage}
      />
    </UserNameDisplayContextProvider>
  ) : null
}

export function IssueViewerInternalFragment({
  viewerFragment,
  issueFragment,
  optionConfig,
  containerRef,
  isRepoOwnerEnterpriseManaged,
  secondaryIssueData,
  timelinePage,
}: IssueViewerInternalFragmentProps) {
  const issue = useFragment(issueViewerViewFragment, issueFragment)
  const viewer = useFragment(issueViewerViewerFragment, viewerFragment) || null

  const hasSubIssues = useHasSubIssues(issue)
  const canEditSubIssues = useCanEditSubIssues(issue)
  const {hideTimeline = false} = optionConfig

  const showEmuContributionBlockedBanner =
    viewer && !!viewer.enterpriseManagedEnterpriseId && !isRepoOwnerEnterpriseManaged
  const showCommentComposer = viewer && !showEmuContributionBlockedBanner && !optionConfig.isReadonly

  const [successBannerState, setSuccessBannerState] = useState<{
    show: boolean
    actionType?: 'request' | 'enable'
    orgLogin?: string
  }>({
    show: false,
  })

  const handleRequestSuccess = useCallback((actionType?: 'request' | 'enable', orgLogin?: string) => {
    setSuccessBannerState({show: true, actionType, orgLogin})
  }, [])

  const composerRef = useRef<MarkdownComposerRef>(null)
  const {startCommentEdit, cancelCommentEdit, isCommentEditActive} = useCommentEditsContext()
  const issueBodyKey = `issue-${issue.id}-body`
  const {setInputElementState, clearInputElementStates} = useInputElementActiveContext()
  const {anyItemPickerOpen} = useItemPickersContext()
  // eslint-disable-next-line react-hooks/refs
  const breakpoint = useContainerBreakpoint(containerRef.current)

  // Reset comment and input states on issue change
  useEffect(() => {
    clearInputElementStates()
  }, [issue.id, clearInputElementStates])

  useEffect(() => {
    setInputElementState(IDS.itemPicker, anyItemPickerOpen())
  }, [anyItemPickerOpen, setInputElementState])

  // Whenever the issue body editing state changes, synchronize that state via callbacks
  const onIssueEditStateChange = optionConfig.onIssueEditStateChange
  useEffect(() => {
    onIssueEditStateChange?.(isCommentEditActive())
  }, [isCommentEditActive, onIssueEditStateChange])

  const onCommentReply = useCallback((quotedComment: string) => {
    composerRef.current?.setText(quotedComment)
    setTimeout(() => composerRef.current?.focus(), 0)
  }, [])

  const onCommentChange = useCallback(
    (id: string) => {
      startCommentEdit(COMMENTING_VALUES.localStorageKeys.issueComment('', issue.id, id))
      optionConfig.onCommentEditStart?.(id)
    },
    [issue.id, optionConfig, startCommentEdit],
  )
  const onCommentEditCancel = useCallback(
    (id: string) => {
      cancelCommentEdit(COMMENTING_VALUES.localStorageKeys.issueComment('', issue.id, id))
      optionConfig.onCommentEditCancel?.(id)
    },
    [cancelCommentEdit, issue.id, optionConfig],
  )

  const beforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (isCommentEditActive()) {
        event.preventDefault()
        return (event.returnValue = '')
      }
    },
    [isCommentEditActive],
  )

  useBeforeUnload(beforeUnload)

  const metadataContent = useMemo(
    () => (
      <IssueSidebar
        sidebarKey={issue}
        sidebarSecondaryKey={secondaryIssueData}
        viewer={viewer}
        optionConfig={optionConfig}
        onCopilotAccessRequestSuccess={handleRequestSuccess}
      />
    ),
    [issue, optionConfig, secondaryIssueData, viewer, handleRequestSuccess],
  )

  const metadataPane = useMemo(
    () => (
      <div className={styles.issueViewerMetadataPane} data-testid={TEST_IDS.issueViewerMetadataPane}>
        <Heading as="h2" className={styles.metadataHeader}>
          {LABELS.metadataHeader}
        </Heading>
        <Heading as="h2" className={`${styles.largeScreenMetadataHeader} sr-only`}>
          {LABELS.metadataHeader}
        </Heading>
        {metadataContent}
      </div>
    ),
    [metadataContent],
  )

  const handleBodyEditStateChange = useCallback(
    (isEditing: boolean) => {
      optionConfig?.onIssueEditStateChange?.(isEditing)
      if (isEditing) {
        startCommentEdit(issueBodyKey)
      } else {
        cancelCommentEdit(issueBodyKey)
      }
    },
    [cancelCommentEdit, issueBodyKey, optionConfig, startCommentEdit],
  )
  // This is used to subscribe to hash-change events
  // Specifically, it is used to detect when the user has (soft) navigated to a comment in the same issue
  useHash()

  const {createDialogOpen, activeIssueId, closeCreateDialog} = useSubIssueState()

  return (
    <>
      {secondaryIssueData?.discussion && (
        <ConvertedToDiscussionBanner discussionUrl={secondaryIssueData?.discussion?.url ?? ''} />
      )}
      {successBannerState.show && (
        <CopilotCodingAgentRequestAccessSuccessBanner
          actionType={successBannerState.actionType}
          orgLogin={successBannerState.orgLogin}
          onDismiss={() => setSuccessBannerState({show: false})}
        />
      )}
      <Header
        issue={issue}
        issueSecondary={secondaryIssueData}
        optionConfig={optionConfig}
        containerRef={containerRef}
      />
      <ContentWrapper sx={optionConfig?.innerSx}>
        <Box
          sx={{
            flexDirection: optionConfig.useViewportQueries
              ? ['column', 'column', 'row', 'row']
              : breakpoint(['column', 'column', 'row', 'row']),
          }}
          className={styles.contentAndSidebarWrapper}
        >
          <Box
            sx={{
              width: optionConfig.useViewportQueries
                ? ['100%', '100%', 'auto', 'auto']
                : breakpoint(['100%', '100%', 'auto', 'auto']),
            }}
            className={styles.contentArea}
          >
            <div data-testid={TEST_IDS.issueViewerIssueContainer}>
              <IssueBody
                issue={issue}
                secondaryKey={secondaryIssueData}
                onLinkClick={optionConfig.onLinkClick}
                onIssueEditStateChange={handleBodyEditStateChange}
                onIssueUpdate={optionConfig.onIssueUpdate}
                onCommentReply={onCommentReply}
                isIssueEditActive={isCommentEditActive}
                highlightedEventText={ssrSafeLocation.hash}
                insideSidePanel={optionConfig.insideSidePanel}
                isReadonly={optionConfig.isReadonly || false}
              />
            </div>
            {hasSubIssues && (
              <Box
                sx={{
                  ml: breakpoint(['0px', '0px', '56px', '56px']),
                }}
                data-testid={TEST_IDS.subIssuesIssueContainer}
                className={styles.subIssuesContainer}
              >
                <SubIssuesList
                  issueKey={secondaryIssueData}
                  onSubIssueClick={optionConfig.onSubIssueClick}
                  insideSidePanel={optionConfig.insideSidePanel}
                  readonly={!canEditSubIssues || optionConfig.isReadonly}
                />
              </Box>
            )}
            <div data-testid={TEST_IDS.issueViewerCommentsContainer} className={CLASS_NAMES.commentsContainer}>
              <div className={styles.commentsContainer}>
                <ErrorBoundary fallback={<IssueTimelineErrorFallback />} critical>
                  <Suspense fallback={<IssueTimelineLoading delayedShow />}>
                    {!hideTimeline && (
                      <IssueTimeline
                        issue={issue}
                        issueSecondary={secondaryIssueData}
                        viewer={viewer?.login ?? null}
                        highlightedEvent={getHighlightedEventText(ssrSafeLocation.hash, ISSUE_EVENTS)}
                        onCommentReply={onCommentReply}
                        onCommentChange={onCommentChange}
                        onCommentEditCancel={onCommentEditCancel}
                        optionConfig={optionConfig}
                        page={timelinePage}
                      />
                    )}
                    {showCommentComposer && (
                      <IssueCommentComposer
                        ref={composerRef}
                        issue={issue}
                        issueSecondary={secondaryIssueData}
                        viewer={viewer}
                        onChange={() => {
                          startCommentEdit(COMMENTING_VALUES.localStorageKeys.issueNewComment('viewer', issue.id))
                          optionConfig.onCommentEditStart?.(IDS.newComment)
                        }}
                        onSave={() => {
                          cancelCommentEdit(COMMENTING_VALUES.localStorageKeys.issueNewComment('viewer', issue.id))
                          optionConfig.onCommentEditCancel?.(IDS.newComment)
                        }}
                        onCancel={() => {
                          cancelCommentEdit(COMMENTING_VALUES.localStorageKeys.issueNewComment('viewer', issue.id))
                          optionConfig.onCommentEditCancel?.(IDS.newComment)
                        }}
                        onNewIssueComment={optionConfig.onNewIssueComment}
                        fileUploadsEnabled
                        insideSidePanel={optionConfig.insideSidePanel ?? false}
                      />
                    )}
                    {!viewer && <SignedOutBanner />}
                  </Suspense>
                </ErrorBoundary>
                {showEmuContributionBlockedBanner && <EmuContributionBlockedBanner />}
              </div>
            </div>
          </Box>

          <Box
            sx={{
              width: optionConfig.useViewportQueries
                ? ['auto', 'auto', '256px', '296px']
                : breakpoint(['auto', 'auto', '256px', '296px']),
            }}
            data-testid={TEST_IDS.issueViewerMetadataContainer}
            className={styles.metadataSidebar}
          >
            {metadataPane}
          </Box>
        </Box>
      </ContentWrapper>
      {secondaryIssueData && createDialogOpen && activeIssueId === issue.id && (
        <SubIssuesCreateDialog
          open={createDialogOpen}
          setOpen={closeCreateDialog}
          issue={secondaryIssueData}
          onCreateSuccess={({createMore}): void => {
            if (!createMore) {
              closeCreateDialog()
            }
          }}
        />
      )}
    </>
  )
}

function IsssueViewerWithSecondary({owner, repo, number, ...rest}: IssueViewerWithSecondaryProps) {
  const [secondaryIssueData] = useSecondaryQuery({owner, repo, number})

  return <IssueViewerInternal secondaryIssueData={secondaryIssueData ?? undefined} {...rest} />
}

try{ IssueViewer.displayName ||= 'IssueViewer' } catch {}
try{ IssueViewerInternal.displayName ||= 'IssueViewerInternal' } catch {}
try{ IssueViewerInternalFragment.displayName ||= 'IssueViewerInternalFragment' } catch {}
try{ IsssueViewerWithSecondary.displayName ||= 'IsssueViewerWithSecondary' } catch {}